import { css } from '@emotion/react';
import { color, Icon, spacing, Typography } from '@uniquegood/realworld-studio-design';

import { ACTION_TYPE_METADATA, conditionType } from '@/components/ActionHandler/utils';
import {
  reactionCommandDefinitionGroup,
  reactionConditionDefinitions
} from '@/components/ActionHandler/utils/ReactionDefinitions';

type PreviewStatus = 'ready' | 'needsCheck' | 'blocked';
type IssueLevel = 'error' | 'warning' | 'info';

interface PreviewIssue {
  level: IssueLevel;
  message: string;
}

interface QuestOperationPreviewProps {
  actionType?: ActionHandler['actionType'];
  actionParameters?: ActionHandler['actionParameters'];
  reactionDefinitions: ActionHandler['reactionDefinitions'];
  failureCommands: ActionHandler['failureCommands'];
}

const WEBVIEW_ACTION_TYPE = 'RealWorld.ActionTypes.WebView';
const DUMMY_ACTION_TYPE = 'RealWorld.ActionTypes.Dummy';

const ADVANCED_ACTION_TYPES = new Set([
  'RealWorld.ActionTypes.AR',
  'RealWorld.ActionTypes.QR',
  'RealWorld.ActionTypes.FrameShot',
  'RealWorld.ActionTypes.Nfc',
  'RealWorld.ActionTypes.GpsRadar',
  'RealWorld.ActionTypes.ColorFilter',
  'RealWorld.ActionTypes.CameraOverlap',
  'RealWorld.ActionTypes.FrameShotV2',
  'RealWorld.ActionTypes.GpsMap',
  'RealWorld.ActionTypes.ImageDetection',
  'RealWorld.ActionTypes.ImageDetection.Scratch'
]);

const commandDefinitions = Object.values(reactionCommandDefinitionGroup).flatMap(
  (group) => group.items
);

const QuestOperationPreview = ({
  actionType,
  actionParameters,
  reactionDefinitions,
  failureCommands
}: QuestOperationPreviewProps) => {
  const issues = getPreviewIssues({
    actionType,
    actionParameters,
    reactionDefinitions,
    failureCommands
  });
  const status = getStatus(issues);
  const actionLabel = getActionLabel(actionType);
  const hasImmediateReaction = reactionDefinitions.some(isImmediateReactionDefinition);

  return (
    <aside css={PreviewPanel} aria-label="퀘스트 작동 미리보기">
      <div css={PreviewHeader}>
        <div>
          <Typography as="h3" type="button">
            퀘스트 작동 미리보기
          </Typography>
          <Typography as="p" type="caption" textColor="subdued">
            저장 전 현재 설정 기준으로 실행 흐름을 점검해요
          </Typography>
        </div>
        <span css={StatusBadge(status)}>
          <Icon icon={getStatusIcon(status)} size="14px" />
          {getStatusLabel(status)}
        </span>
      </div>

      <dl css={SummaryList}>
        <div>
          <dt>플레이어 액션</dt>
          <dd>{actionLabel}</dd>
        </div>
        <div>
          <dt>기본 흐름</dt>
          <dd>
            {hasImmediateReaction
              ? '조건 없음 리액션 포함'
              : '조건을 충족한 리액션만 실행'}
          </dd>
        </div>
      </dl>

      <section css={SectionSpacing}>
        <Typography as="h4" type="caption" textColor="subdued" cssStyle={SectionTitle}>
          점검 결과
        </Typography>
        <ul css={IssueList}>
          {issues.map((issue) => (
            <li key={issue.message} css={IssueItem(issue.level)}>
              {issue.message}
            </li>
          ))}
        </ul>
      </section>

      <section css={SectionSpacing}>
        <Typography as="h4" type="caption" textColor="subdued" cssStyle={SectionTitle}>
          리액션 흐름
        </Typography>
        <ol css={ReactionList}>
          {reactionDefinitions.length === 0 && (
            <li>
              <Typography type="caption" textColor="subdued">
                조건별 리액션이 아직 없어요
              </Typography>
            </li>
          )}
          {reactionDefinitions.map((reaction, index) => (
            <li key={reaction.key}>
              <Typography type="caption" cssStyle={ReactionTitle}>
                {index + 1}. {formatConditions(reaction.conditions)}
              </Typography>
              <Typography type="caption" textColor="subdued">
                {formatCommands(reaction.commands)}
              </Typography>
            </li>
          ))}
        </ol>
      </section>

      {failureCommands.length > 0 && (
        <section css={SectionSpacing}>
          <Typography as="h4" type="caption" textColor="subdued" cssStyle={SectionTitle}>
            오답 시 리액션
          </Typography>
          <Typography type="caption">{formatCommands(failureCommands)}</Typography>
        </section>
      )}
    </aside>
  );
};

export default QuestOperationPreview;

function getPreviewIssues({
  actionType,
  actionParameters,
  reactionDefinitions,
  failureCommands
}: QuestOperationPreviewProps): PreviewIssue[] {
  const issues: PreviewIssue[] = [];

  if (!actionType || actionType === DUMMY_ACTION_TYPE) {
    issues.push({
      level: 'info',
      message: '플레이어 액션 없이 퀘스트 버튼을 누르면 리액션부터 실행돼요.'
    });
  }

  if (actionType === WEBVIEW_ACTION_TYPE && isBlank(actionParameters?.content)) {
    issues.push({
      level: 'error',
      message: '웹뷰 HTML이 비어 있어요. 웹뷰 빌더에서 내용을 반영해주세요.'
    });
  }

  if (actionType === WEBVIEW_ACTION_TYPE && !reactionDefinitions.some(isImmediateReactionDefinition)) {
    issues.push({
      level: 'warning',
      message:
        '웹뷰가 별도 입력값을 보내지 않는 구조라면 실행 조건 없음 리액션이 있어야 다음 흐름으로 넘어가요.'
    });
  }

  if (actionType && ADVANCED_ACTION_TYPES.has(actionType)) {
    issues.push({
      level: 'info',
      message: '카메라, GPS, NFC 기능은 실제 기기 권한과 센서 환경에서 최종 확인이 필요해요.'
    });
  }

  if (reactionDefinitions.length === 0) {
    issues.push({
      level: 'error',
      message: '조건별 리액션이 없어 액션 성공 후 실행할 내용이 없어요.'
    });
  }

  reactionDefinitions.forEach((reaction, index) => {
    if (reaction.commands.length === 0) {
      issues.push({
        level: 'error',
        message: `조건별 리액션 ${index + 1}에 실행할 리액션 명령이 없어요.`
      });
    }
  });

  if (failureCommands.length === 0) {
    issues.push({
      level: 'info',
      message: '오답 시 리액션이 없으면 실패 상황에서 별도 안내 없이 머물 수 있어요.'
    });
  }

  if (issues.length === 0) {
    issues.push({
      level: 'info',
      message: '현재 설정에서 즉시 발견되는 누락은 없어요.'
    });
  }

  return issues;
}

function getStatus(issues: PreviewIssue[]): PreviewStatus {
  if (issues.some((issue) => issue.level === 'error')) {
    return 'blocked';
  }

  if (issues.some((issue) => issue.level === 'warning')) {
    return 'needsCheck';
  }

  return 'ready';
}

function getActionLabel(actionType?: string) {
  if (!actionType) {
    return '액션 미설정';
  }

  return ACTION_TYPE_METADATA[actionType]?.label ?? actionType;
}

function getConditionLabel(conditionType: string) {
  return (
    reactionConditionDefinitions.find((definition) => definition.type === conditionType)?.label ??
    conditionType
  );
}

function getCommandLabel(commandType: string) {
  return commandDefinitions.find((definition) => definition.type === commandType)?.label ?? commandType;
}

function isImmediateReactionDefinition(reaction: ReactionDefinition) {
  return (
    reaction.conditions.length === 0 ||
    reaction.conditions.some((condition) => condition.type === conditionType.none)
  );
}

function formatConditions(conditions: ReactionCondition[]) {
  if (conditions.length === 0) {
    return '실행 조건 없음';
  }

  return conditions.map((condition) => getConditionLabel(condition.type)).join(' + ');
}

function formatCommands(commands: ReactionCommand[]) {
  if (commands.length === 0) {
    return '실행할 명령 없음';
  }

  return commands.map((command) => getCommandLabel(command.type)).join(' → ');
}

function isBlank(value: unknown) {
  return typeof value !== 'string' || value.trim().length === 0;
}

function getStatusLabel(status: PreviewStatus) {
  if (status === 'blocked') {
    return '수정 필요';
  }

  if (status === 'needsCheck') {
    return '확인 필요';
  }

  return '정상';
}

function getStatusIcon(status: PreviewStatus) {
  if (status === 'blocked') {
    return 'exclamation_circle_solid';
  }

  if (status === 'needsCheck') {
    return 'info_circle_solid';
  }

  return 'check_solid';
}

const PreviewPanel = css`
  margin-bottom: 48px;
  padding: ${spacing.common.medium};
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  background-color: ${color.surface_default_default};
`;

const PreviewHeader = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.common.medium};
  margin-bottom: ${spacing.common.medium};
`;

const StatusBadge = (status: PreviewStatus) => {
  const styles = {
    blocked: {
      backgroundColor: color.surface_critical_subdued,
      color: color.text_critical
    },
    needsCheck: {
      backgroundColor: color.palette_secondary_yellow_tint_02,
      color: color.text_default
    },
    ready: {
      backgroundColor: color.palette_primary_purple_tint_01,
      color: color.action_primary_default
    }
  }[status];

  return css`
    display: inline-flex;
    align-items: center;
    gap: ${spacing.common.xsmall};
    flex-shrink: 0;
    padding: 4px ${spacing.common.small};
    border-radius: ${spacing.borderRadius.small};
    color: ${styles.color};
    background-color: ${styles.backgroundColor};
    font-size: 12px;
    font-weight: 600;

    svg {
      margin: 0;
    }
  `;
};

const SummaryList = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.common.small};
  margin: 0;
  padding: ${spacing.common.small};
  border-radius: ${spacing.borderRadius.small};
  background-color: ${color.surface_default_subdued};

  div {
    display: grid;
    grid-template-columns: 88px 1fr;
    gap: ${spacing.common.small};
  }

  dt,
  dd {
    margin: 0;
    font-size: 13px;
    line-height: 20px;
  }

  dt {
    color: ${color.text_subdued};
  }

  dd {
    color: ${color.text_default};
  }
`;

const SectionSpacing = css`
  margin-top: ${spacing.common.medium};
`;

const SectionTitle = css`
  margin-bottom: ${spacing.common.xsmall};
`;

const IssueList = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.xsmall};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const IssueItem = (level: IssueLevel) => {
  const borderColor = level === 'error' ? color.border_critical_subdued : color.border_default_subdued;
  const backgroundColor =
    level === 'error' ? color.surface_critical_subdued : color.surface_default_subdued;
  const textColor = level === 'error' ? color.text_critical : color.text_default;

  return css`
    padding: ${spacing.common.small};
    border: 1px solid ${borderColor};
    border-radius: ${spacing.borderRadius.small};
    background-color: ${backgroundColor};
    color: ${textColor};
    font-size: 13px;
    line-height: 20px;
  `;
};

const ReactionList = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.small};
  margin: 0;
  padding-left: 0;
  list-style: none;
`;

const ReactionTitle = css`
  display: block;
  margin-bottom: 2px;
`;
